import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  UploadCloud,
  FileText,
  X,
  Plus,
  ChevronDown,
  FileSignature,
  UserCheck,
  Copy,
  Mail,
  MessageSquare,
  Tag,
  Send,
  Check,
  Loader2,
  GripVertical,
  Building2,
  Search as SearchIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { uploadDocument, getUsers } from "../../api";
import { companyName, companyAccent } from "../../companies";

const ROLES = [
  { key: "Needs to Sign", icon: FileSignature },
  { key: "Receives a Copy", icon: Copy },
];

const CATEGORIES = [
  "Human Resources",
  "Legal",
  "Finance",
  "Sales",
  "Onboarding",
  "Procurement",
];

// The company to show for a person: the sender's active company if they belong
// to it, otherwise their first company. Used for the badge/chip.
const primaryCompanyKey = (u, activeKey) =>
  (u?.companies || []).includes(activeKey) ? activeKey : (u?.companies || [])[0];

const CompanyChip = ({ companyKey, cross, multi, className = "" }) => {
  if (multi) {
    return (
      <span
        data-testid="company-chip-multiple"
        className={`inline-flex shrink-0 items-center gap-1 rounded-full bg-[#EFE9E2] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#8B8680] dark:bg-white/10 dark:text-white/55 ${className}`}
        title="Belongs to multiple companies"
      >
        <Building2 className="h-2.5 w-2.5" strokeWidth={2.4} />
        Multiple companies
      </span>
    );
  }
  if (!companyKey) return null;
  const accent = companyAccent(companyKey);
  return (
    <span
      data-testid={`company-chip-${companyKey}`}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        cross
          ? ""
          : "bg-[#EFE9E2] text-[#8B8680] dark:bg-white/10 dark:text-white/55"
      } ${className}`}
      style={cross ? { background: `${accent}22`, color: accent } : undefined}
      title={cross ? `Different company: ${companyName(companyKey)}` : companyName(companyKey)}
    >
      <Building2 className="h-2.5 w-2.5" strokeWidth={2.4} />
      {companyName(companyKey)}
    </span>
  );
};

// Interactive company selector shown on a multi-company recipient's row. The
// sender must pick one of that person's companies before the envelope can send.
const RecipientCompanyDropdown = ({ r, activeKey, onPick }) => {
  const chosen = r.company;
  const cross = chosen && chosen !== activeKey;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`recipient-company-dd-${r.email}`}
          className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide outline-none ${
            chosen
              ? cross
                ? ""
                : "bg-[#EFE9E2] text-[#8B8680] dark:bg-white/10 dark:text-white/55"
              : "bg-[#F79009]/15 text-[#B54708] dark:bg-[#F79009]/20 dark:text-[#FDB022]"
          }`}
          style={chosen && cross ? { background: `${companyAccent(chosen)}22`, color: companyAccent(chosen) } : undefined}
        >
          <Building2 className="h-2.5 w-2.5" strokeWidth={2.4} />
          {chosen ? companyName(chosen) : "Select company"}
          <ChevronDown className="h-2.5 w-2.5" strokeWidth={2.6} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        style={{ fontFamily: "Urbanist, sans-serif" }}
        className="w-48 rounded-2xl border-[#E7E0D8] bg-white p-1.5 shadow-lg dark:border-white/10 dark:bg-[#1A1721]"
      >
        {(r.companies || []).map((k) => (
          <DropdownMenuItem
            key={k}
            data-testid={`recipient-company-opt-${r.email}-${k}`}
            onClick={() => onPick(k)}
            className="flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-[#1A1815] focus:bg-[#F0EBE5] dark:text-white dark:focus:bg-white/10"
          >
            <Building2 className="h-4 w-4" strokeWidth={1.8} />
            {companyName(k)}
            {chosen === k && <Check className="ml-auto h-4 w-4" strokeWidth={2.4} />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const PersonRow = ({ u, activeKey, onSelect }) => {
  const multi = (u.companies || []).length > 1;
  const key = multi ? null : primaryCompanyKey(u, activeKey);
  const cross = key && key !== activeKey;
  return (
    <button
      type="button"
      data-testid={`recipient-user-${u.email}`}
      onClick={() => onSelect(u)}
      className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm font-medium text-[#1A1815] transition-colors hover:bg-[#F0EBE5] dark:text-white dark:hover:bg-white/10"
    >
      <img src={u.avatar} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate">{u.name}</span>
        <span className="truncate text-[11px] text-[#8B8680] dark:text-white/40">
          {u.role} · {multi ? "Multiple companies" : companyName(key)}
        </span>
      </span>
      <CompanyChip companyKey={key} cross={cross} multi={multi} className="ml-auto" />
    </button>
  );
};

const PickerHeading = ({ children }) => (
  <p className="px-2.5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-[#B0A99F] dark:text-white/35">
    {children}
  </p>
);

// Searchable people picker (custom popover so the search input isn't captured by
// a menu's typeahead). Lists everyone across QGroup, grouped active-company-first.
const PeoplePicker = ({ options, activeKey, onSelect, trigger, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const ql = q.trim().toLowerCase();
  const fil = options.filter(
    (u) => !ql || `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(ql)
  );
  const mine = fil.filter((u) => (u.companies || []).includes(activeKey));
  const others = fil.filter((u) => !(u.companies || []).includes(activeKey));
  const pick = (u) => {
    onSelect(u);
    setOpen(false);
    setQ("");
  };
  return (
    <div ref={ref} className={`relative ${className}`}>
      <div onClick={() => setOpen((o) => !o)}>{trigger}</div>
      {open && (
        <div
          data-testid="recipient-picker-panel"
          style={{ fontFamily: "Urbanist, sans-serif" }}
          className="absolute left-0 z-50 mt-2 w-80 rounded-2xl border border-[#E7E0D8] bg-white p-1.5 shadow-xl dark:border-white/10 dark:bg-[#141019]"
        >
          <div className="mb-1 flex items-center gap-2 rounded-xl border border-[#E7E0D8] bg-[#FAF6F1] px-3 py-2 dark:border-white/10 dark:bg-white/5">
            <SearchIcon className="h-4 w-4 shrink-0 text-[#8B8680] dark:text-white/40" strokeWidth={1.8} />
            <input
              data-testid="recipient-search-input"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search all QGroup people"
              className="w-full bg-transparent text-sm font-medium text-[#1A1815] outline-none placeholder:text-[#B0A99F] dark:text-white dark:placeholder:text-white/35"
            />
          </div>
          <div className="thin-scroll max-h-[300px] overflow-y-auto">
            {fil.length === 0 ? (
              <p className="px-3 py-2.5 text-sm text-[#8B8680] dark:text-white/40">
                No people found.
              </p>
            ) : (
              <>
                {mine.length > 0 && (
                  <>
                    <PickerHeading>{companyName(activeKey)}</PickerHeading>
                    {mine.map((u) => (
                      <PersonRow key={u.id || u.email} u={u} activeKey={activeKey} onSelect={pick} />
                    ))}
                  </>
                )}
                {others.length > 0 && (
                  <>
                    <PickerHeading>Other QGroup companies</PickerHeading>
                    {others.map((u) => (
                      <PersonRow key={u.id || u.email} u={u} activeKey={activeKey} onSelect={pick} />
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SectionCard = ({ number, icon: Icon, title, subtitle, required, children }) => (
  <div className="glass-panel rounded-[22px] p-6 md:p-7">
    <div className="mb-5 flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] dark:bg-white/10 dark:text-white">
        <Icon className="h-5 w-5" strokeWidth={1.7} />
      </div>
      <div>
        <h3 className="text-lg font-bold tracking-tight text-[#1A1815] dark:text-white">
          <span className="mr-2 text-[#B0A99F] dark:text-white/40">{number}</span>
          {title}
          {required && <span className="ml-1 text-[#E5484D]">*</span>}
        </h3>
        {subtitle && (
          <p className="text-sm text-[#8B8680] dark:text-white/45">{subtitle}</p>
        )}
      </div>
    </div>
    {children}
  </div>
);

const RoleDropdown = ({ value, onChange }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        data-testid="role-dropdown-btn"
        className="flex shrink-0 items-center gap-2 rounded-xl border border-[#E7E0D8] bg-[#F6EFE8] px-4 py-2.5 text-sm font-medium text-[#1A1815] outline-none transition-colors hover:bg-[#F0EBE5] dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
      >
        {value}
        <ChevronDown className="h-4 w-4" strokeWidth={2.2} />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="start"
      style={{ fontFamily: "Urbanist, sans-serif" }}
      className="w-52 rounded-2xl border-[#E7E0D8] bg-white p-1.5 shadow-lg dark:border-white/10 dark:bg-[#1A1721]"
    >
      {ROLES.map(({ key, icon: Icon }) => (
        <DropdownMenuItem
          key={key}
          data-testid={`role-option-${key.replace(/\s+/g, "-").toLowerCase()}`}
          onClick={() => onChange(key)}
          className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[#1A1815] focus:bg-[#F0EBE5] dark:text-white dark:focus:bg-white/10"
        >
          <Icon className="h-4 w-4" strokeWidth={1.8} />
          {key}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const SendEnvelope = ({ onAction, user, company }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Restore everything when returning from the Add Fields step (back navigation).
  const prev = location.state || {};
  const activeKey = company?.key;
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState(prev.files || []);
  const [dragging, setDragging] = useState(false);
  const [users, setUsers] = useState([]);
  const [recipients, setRecipients] = useState(
    prev.recipients && prev.recipients.length
      ? prev.recipients
      : [{ id: "", name: "", email: "", role: "Needs to Sign" }]
  );
  const [subject, setSubject] = useState(prev.subject || "");
  const [message, setMessage] = useState(prev.message || "");
  const [category, setCategory] = useState(prev.category ?? null);
  const [orderEnabled, setOrderEnabled] = useState(!!prev.orderEnabled);
  // Fields already placed in Add Fields — carried through so they survive a round trip.
  const [placedFields] = useState(prev.fields || []);
  const [dragIdx, setDragIdx] = useState(null);

  const moveRecipient = (from, to) => {
    if (from == null || to == null || from === to) return;
    setRecipients((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  useEffect(() => {
    getUsers().then(setUsers).catch(() => {});
  }, []);

  const availableUsers = users.filter((u) => u.email !== user?.email);

  const addFiles = (list) => {
    const arr = Array.from(list);
    arr.forEach((f) => {
      const tempId = `${f.name}-${Date.now()}-${Math.random()}`;
      setFiles((prev) => [
        ...prev,
        { tempId, name: f.name, size: "", uploading: true, progress: 0 },
      ]);
      uploadDocument(f, (p) =>
        setFiles((prev) =>
          prev.map((x) => (x.tempId === tempId ? { ...x, progress: p } : x))
        )
      )
        .then((res) => {
          setFiles((prev) => {
            const next = prev.map((x) =>
              x.tempId === tempId ? { ...res, tempId, uploading: false } : x
            );
            if (!subject.trim()) {
              setSubject(`Complete with Qosign: ${res.name}`);
            }
            return next;
          });
        })
        .catch(() => {
          setFiles((prev) => prev.filter((x) => x.tempId !== tempId));
          onAction(`Failed to upload ${f.name}`);
        });
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const updateRecipient = (i, field, val) => {
    setRecipients((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r))
    );
  };

  const pickRecipient = (i) => (u) => {
    const comps = u.companies || [];
    setRecipients((prev) =>
      prev.map((x, idx) =>
        idx === i
          ? {
              ...x,
              id: u.id,
              name: u.name,
              email: u.email,
              avatar: u.avatar,
              companies: comps,
              // Single-company → auto-select it; multi-company → force an explicit pick.
              company: comps.length === 1 ? comps[0] : "",
            }
          : x
      )
    );
  };

  const inputClass =
    "border-[#E7E0D8] bg-white text-[#1A1815] placeholder:text-[#B0A99F] dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/35";

  return (
    <div className="px-6 pt-6 md:px-10 md:pt-8">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/")}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:border dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
          </button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#1A1815] dark:text-white">
              Send an Envelope
            </h1>
            <p className="text-sm text-[#8B8680] dark:text-white/45">
              Prepare documents, recipients and details
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            if (files.length === 0) {
              onAction("Please add at least one document");
              return;
            }
            if (files.some((f) => f.uploading)) {
              onAction("Please wait for uploads to finish");
              return;
            }
            const validRecipients = recipients.filter(
              (r) => r.name.trim() && r.email.trim()
            );
            if (validRecipients.length === 0) {
              onAction("Please add at least one recipient (name & email)");
              return;
            }
            const needsCompany = validRecipients.find(
              (r) => (r.companies || []).length > 1 && !r.company
            );
            if (needsCompany) {
              onAction(`Choose a company for ${needsCompany.name}`);
              return;
            }
            if (!subject.trim()) {
              onAction("Please add a subject");
              return;
            }
            if (!message.trim()) {
              onAction("Please add a message");
              return;
            }
            navigate("/add-fields", {
              state: {
                files,
                recipients: validRecipients,
                subject,
                message,
                category,
                orderEnabled,
                fields: placedFields,
              },
            });
          }}
          className="flex items-center gap-2 rounded-xl bg-[#1A1815] px-7 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03] active:scale-95 dark:bg-white dark:text-black"
        >
          Add fields
          <ArrowRight className="h-4 w-4" strokeWidth={1.9} />
        </button>
      </header>

      {/* Sections */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* 1. Add Documents */}
        <SectionCard number="01" icon={UploadCloud} title="Add Documents" subtitle="Upload the files to be signed" required>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
              dragging
                ? "border-[#1A1815] bg-[#F0EBE5] dark:border-white/40 dark:bg-white/10"
                : "border-[#E7E0D8] bg-[#F6EFE8] dark:border-white/15 dark:bg-white/[0.03]"
            }`}
          >
            <UploadCloud className="h-8 w-8 text-[#8B8680] dark:text-white/50" strokeWidth={1.6} />
            <p className="mt-3 text-sm font-semibold text-[#1A1815] dark:text-white">
              Drag &amp; drop files here
            </p>
            <p className="text-xs text-[#8B8680] dark:text-white/45">
              or click to upload (PDF or photo)
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
          </div>
          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              {files.map((f, i) => (
                <div
                  key={f.tempId || i}
                  className="flex items-center gap-3 rounded-xl bg-[#F6EFE8] px-3 py-2.5 dark:bg-white/[0.04]"
                >
                  {f.uploading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-[#7A6FF0]" strokeWidth={1.9} />
                  ) : (
                    <FileText className="h-5 w-5 text-[#7A6FF0]" strokeWidth={1.7} />
                  )}
                  <span className="flex-1 truncate text-sm font-medium text-[#1A1815] dark:text-white">
                    {f.name}
                  </span>
                  <span className="text-xs text-[#8B8680] dark:text-white/45">
                    {f.uploading ? `${f.progress}%` : f.size}
                  </span>
                  <button
                    onClick={() =>
                      setFiles(files.filter((x, idx) => idx !== i))
                    }
                    className="text-[#8B8680] hover:text-[#B42318] dark:text-white/45"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* 2. Add Recipients */}
        <SectionCard number="02" icon={UserCheck} title="Add Recipients" subtitle="Who needs to receive this envelope" required>
          <div className="mb-4 flex items-center justify-between rounded-2xl bg-[#F6EFE8] px-4 py-3 dark:bg-white/[0.04]">
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-[#1A1815] dark:text-white">
                Set signing order
              </span>
              <span className="text-xs text-[#8B8680] dark:text-white/45">
                Recipients sign in the numbered sequence
              </span>
            </div>
            <Switch checked={orderEnabled} onCheckedChange={setOrderEnabled} />
          </div>
          <div className="space-y-4">
            {recipients.map((r, i) => {
              const chosen = recipients
                .filter((_, idx) => idx !== i)
                .map((x) => x.email)
                .filter(Boolean);
              const opts = availableUsers.filter((u) => !chosen.includes(u.email));
              return (
                <div
                  key={i}
                  data-testid={`recipient-row-${i}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    moveRecipient(dragIdx, i);
                    setDragIdx(null);
                  }}
                  className={`space-y-3 rounded-2xl bg-[#F6EFE8] p-4 transition-shadow dark:bg-white/[0.04] ${
                    dragIdx === i ? "ring-2 ring-[#1A1815] dark:ring-white/40" : ""
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      data-testid={`recipient-drag-${i}`}
                      draggable
                      onDragStart={() => setDragIdx(i)}
                      onDragEnd={() => setDragIdx(null)}
                      title="Drag to reorder signing order"
                      className="flex h-9 w-7 shrink-0 cursor-grab items-center justify-center rounded-lg text-[#B0A99F] hover:text-[#1A1815] active:cursor-grabbing dark:text-white/35 dark:hover:text-white/70"
                    >
                      <GripVertical className="h-4 w-4" strokeWidth={2} />
                    </span>
                    {orderEnabled && (
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1A1815] text-sm font-bold text-white dark:bg-white dark:text-black">
                        {i + 1}
                      </span>
                    )}
                    {r.email ? (
                      <div className="flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-[#E7E0D8] bg-white px-3 dark:border-white/10 dark:bg-white/5">
                        <img src={r.avatar} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
                        <span className="flex min-w-0 flex-col text-left leading-tight">
                          <span className="truncate text-sm font-medium text-[#1A1815] dark:text-white">{r.name}</span>
                          <span className="truncate text-[11px] text-[#8B8680] dark:text-white/40">{r.email}</span>
                        </span>
                        {(r.companies || []).length > 1 ? (
                          <RecipientCompanyDropdown
                            r={r}
                            activeKey={activeKey}
                            onPick={(key) => updateRecipient(i, "company", key)}
                          />
                        ) : (
                          <CompanyChip
                            companyKey={r.company || (r.companies || [])[0]}
                            cross={(r.company || (r.companies || [])[0]) !== activeKey}
                            className="ml-1"
                          />
                        )}
                        <PeoplePicker
                          options={opts}
                          activeKey={activeKey}
                          onSelect={pickRecipient(i)}
                          className="ml-auto shrink-0"
                          trigger={
                            <button
                              type="button"
                              data-testid={`recipient-change-btn-${i}`}
                              title="Change person"
                              className="flex h-8 w-8 items-center justify-center rounded-full text-[#8B8680] transition-colors hover:bg-[#F0EBE5] hover:text-[#1A1815] dark:text-white/40 dark:hover:bg-white/10 dark:hover:text-white"
                            >
                              <ChevronDown className="h-4 w-4" strokeWidth={2.2} />
                            </button>
                          }
                        />
                      </div>
                    ) : (
                      <PeoplePicker
                        options={opts}
                        activeKey={activeKey}
                        onSelect={pickRecipient(i)}
                        className="flex-1"
                        trigger={
                          <button
                            type="button"
                            data-testid="recipient-picker-btn"
                            className="flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-[#E7E0D8] bg-white px-3 text-sm font-medium text-[#1A1815] outline-none transition-colors hover:bg-[#F0EBE5] dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                          >
                            <span className="text-[#B0A99F] dark:text-white/35">Select a person</span>
                            <ChevronDown className="h-4 w-4 shrink-0" strokeWidth={2.2} />
                          </button>
                        }
                      />
                    )}
                    <RoleDropdown
                      value={r.role}
                      onChange={(val) => updateRecipient(i, "role", val)}
                    />
                    {recipients.length > 1 && (
                      <button
                        onClick={() =>
                          setRecipients(recipients.filter((_, idx) => idx !== i))
                        }
                        className="flex h-9 w-9 items-center justify-center rounded-full text-[#8B8680] hover:text-[#B42318] dark:text-white/45"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            <button
              onClick={() =>
                setRecipients([
                  ...recipients,
                  { id: "", name: "", email: "", role: "Needs to Sign" },
                ])
              }
              className="flex items-center gap-2 rounded-xl border border-[#E7E0D8] px-4 py-2.5 text-sm font-medium text-[#1A1815] transition-colors hover:bg-[#F0EBE5] dark:border-white/10 dark:text-white dark:hover:bg-white/10"
            >
              <Plus className="h-4 w-4" strokeWidth={2} />
              Add recipient
            </button>
          </div>
        </SectionCard>

        {/* 3. Add Message */}
        <SectionCard number="03" icon={MessageSquare} title="Add Message" subtitle="A note for your recipients">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#8B8680] dark:text-white/60">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Please sign your employment agreement"
                className={`h-11 rounded-xl ${inputClass}`}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#8B8680] dark:text-white/60">Message</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write a message to include with your envelope..."
                rows={5}
                className={`rounded-xl ${inputClass}`}
              />
            </div>
          </div>
        </SectionCard>

        {/* 4. Category */}
        <SectionCard number="04" icon={Tag} title="Category" subtitle="Classify this envelope">
          <div className="grid grid-cols-2 gap-3">
            {CATEGORIES.map((cat) => {
              const active = category === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setCategory(active ? null : cat)}
                  className={`flex items-center justify-between rounded-2xl border px-5 py-4 text-left text-sm font-semibold transition-colors ${
                    active
                      ? "border-[#1A1815] bg-[#1A1815] text-white dark:border-white dark:bg-white dark:text-black"
                      : "border-[#E7E0D8] bg-[#F6EFE8] text-[#1A1815] hover:bg-[#F0EBE5] dark:border-white/10 dark:bg-white/5 dark:text-white/80 dark:hover:bg-white/10"
                  }`}
                >
                  {cat}
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors ${
                      active
                        ? "border-white bg-white dark:border-black dark:bg-black"
                        : "border-[#C9C2BA] dark:border-white/30"
                    }`}
                  >
                    {active && (
                      <Check
                        className="h-3 w-3 text-[#1A1815] dark:text-white"
                        strokeWidth={3}
                      />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default SendEnvelope;
